// src/components/pages/PhoneOtpPage.js
import React, { useEffect, useRef } from 'react';
import { db } from "../../firebase";
import { getAuth } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { styled } from '@mui/material/styles';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { isValidPhoneNumber } from 'react-phone-number-input';
import usePhoneAuth from '../../hooks/usePhoneAuth';
import useMessages from "../../hooks/useMessages";
import useFcmTokenSync from "../../hooks/useFcmTokenSync";
import useProfileEditor from '../../hooks/useProfileEditor'; // signOutSafely
import { updateAzureUserPhoneNumber } from "../../services/profileService";

const StyledPhoneInput = styled(PhoneInput)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& .PhoneInputInput': {
    flex: 1,
    height: '56px',
    borderRadius: '50px',
    paddingLeft: theme.spacing(1),
    fontSize: '1rem',
    border: `1px solid ${theme.palette.grey[400]}`,
    outline: 'none',
  },
  '& .PhoneInputCountrySelect': {
    display: 'inline-flex',
    alignItems: 'center',
    height: '56px',
    paddingLeft: theme.spacing(1),
    background: 'transparent',
    border: 0,
    marginRight: theme.spacing(1),
  },
  '& .PhoneInputCountryIcon': { verticalAlign: 'middle' },
}));

export default function PhoneOtpPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const otpInputRef = useRef(null);
  const user = auth.currentUser;

  const { signOutSafely } = useProfileEditor(); // use stronger sign-out

  const { token } = useMessages(!!user?.email);
  useFcmTokenSync(token, user);

  // Skip if already verified
  useEffect(() => {
    if (auth.currentUser?.phoneNumber) {
      navigate('/', { replace: true });
    }
  }, [auth, navigate]);

  const {
    phoneNumber,
    setPhoneNumber,
    otp,
    setOtp,
    status,
    error,
    canResend,
    resendTimer,
    sendCode,
    verifyCode,
    resendCode,
    changeNumber,

    mode,                        // 'link' | 'signin'
    isPhoneAlreadyInUse,         // boolean - only show CTA when true
    startSignInInstead,          // sign out + switch to phone sign-in flow (clears OTP in hook)
  } = usePhoneAuth({
    onSuccess: async () => {
      const current = getAuth().currentUser;
      if (current) {
        await setDoc(
          doc(db, 'users', current.uid),
          { phoneNumber },
          { merge: true }
        );
        await updateAzureUserPhoneNumber(phoneNumber, current?.email);
      }
      navigate('/', { replace: true });
    },
    onError: () => {},
    testMode: false,
    // pass the stronger sign-out; suppress redirect during OTP handoff
    signOutSafely: (opts) =>
      signOutSafely({
        redirect: false,
        preserveAuthHandoff: true,
        ...(opts || {}),
      }),
  });

  const isIdleOrError = status === 'idle' || status === 'error';
  const phoneIsValid = !!phoneNumber && isValidPhoneNumber(phoneNumber);
  const primaryDisabled = status === 'verifying' || !phoneIsValid;

  // SHOW SECONDARY CTA **ONLY** when Firebase confirmed conflict
  const showSecondaryCTA = isPhoneAlreadyInUse;

  // Focus OTP field when code is sent
  useEffect(() => {
    if (status === 'sent' && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [status]);

  // Web OTP API (Android/Chrome/PWA): auto-read the incoming SMS and submit
  useEffect(() => {
    // Only listen when we're expecting a code
    if (status !== 'sent') return;
    if (!('OTPCredential' in window) || !navigator.credentials) return;

    const ac = new AbortController();
    (async () => {
      try {
        const content = await navigator.credentials.get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        });
        if (content && content.code) {
          setOtp(content.code);
          // Automatically verify after autofill
          verifyCode();
        }
      } catch {
        // Ignore aborts/unavailable
      }
    })();

    return () => ac.abort();
  }, [status, setOtp, verifyCode]);

  // Helper: explicit "use a different phone number" reset for a clean start
  const handleUseDifferentNumber = () => {
    // Clear everything and reset to 'link' mode fresh
    setPhoneNumber('');
    changeNumber(); // resets status/otp/conflict flags in the hook
  };

  const handleSkipForNow = () => {
    navigate('/', { replace: true });
  };

  const handleSignOut = async () => {
    try {
      await signOutSafely({ redirect: true });
    } catch {
      // If signOutSafely throws, keep the user on the page (optional: add snackbar later)
    }
  };

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: 'white',
        minHeight: '100%',
        position: 'relative',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
        Verify Your Phone Number
      </Typography>

      {/* off-screen but rendered container for Invisible reCAPTCHA */}
      <div
        id="recaptcha-container"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          top: -100,
          left: -100,
          opacity: 0,
        }}
      />

      {isIdleOrError ? (
        <>
          <StyledPhoneInput
            defaultCountry="US"
            international
            countryCallingCodeEditable={false}
            placeholder="+1 (___) ___-____"
            value={phoneNumber}
            onChange={setPhoneNumber}
            autoFocus
            type="tel"
            aria-invalid={!!error}
            aria-describedby={error ? 'phone-error-text' : undefined}
          />

          {error && (
            <Typography id="phone-error-text" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Primary: send code to link phone to current account */}
          <Button
            variant="contained"
            fullWidth
            onClick={sendCode}
            disabled={primaryDisabled}
            sx={theme => ({
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              borderRadius: '50px',
              padding: '14px 30px',
              mb: showSecondaryCTA ? 1 : 2,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {status === 'verifying' ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Send Code'
            )}
          </Button>

          {/* Secondary: recover by signing in with this phone number (only after conflict) */}
          {showSecondaryCTA && (
            <>
              <Button
                variant="outlined"
                fullWidth
                onClick={startSignInInstead}
                disabled={status === 'verifying'}
                sx={theme => ({
                  backgroundColor: theme.colors.white,
                  color: theme.colors.blue,
                  borderRadius: '50px',
                  border: `1px solid ${theme.colors.blue}`,
                  padding: '12px 30px',
                  mb: 1,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                })}
              >
                Sign in with this phone number
              </Button>
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                We will sign out your current session and sign you in with this phone number.
              </Typography>

              {/* Explicit option to use a different phone number */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleUseDifferentNumber}
                  sx={{ textAlign: 'center' }}
                >
                  Use a different phone number
                </Link>
              </Box>
            </>
          )}
        </>
      ) : (
        <>
          <TextField
            label="Enter Code"
            value={otp}
            onChange={e => setOtp(e.target.value)}
            inputRef={otpInputRef}
            fullWidth
            type="tel"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '50px',
                pl: 1,
              },
            }}
            inputProps={{
              // Helps iOS/Safari show "From Messages" suggestion
              autoComplete: 'one-time-code',
              inputMode: 'numeric',
              pattern: '[0-9]*',
              name: 'one-time-code',
              'aria-invalid': !!error,
              'aria-describedby': error ? 'otp-error-text' : undefined,
            }}
          />

          <Button
            variant="contained"
            fullWidth
            disabled={status === 'verifying'}
            onClick={verifyCode}
            sx={theme => ({
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              borderRadius: '50px',
              padding: '14px 30px',
              mb: 2,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {status === 'verifying' ? (
              <CircularProgress size={20} color="inherit" />
            ) : mode === 'signin' ? (
              'Verify & Sign In'
            ) : (
              'Verify & Link'
            )}
          </Button>

          <Button
            variant="text"
            fullWidth
            disabled={!canResend}
            onClick={resendCode}
            sx={theme => ({
              backgroundColor: theme.colors.white,
              color: theme.colors.blue,
              borderRadius: '50px',
              border: `1px solid ${theme.colors.blue}`,
              padding: '12px 30px',
              mb: 1,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {canResend
              ? "Didn't get a code? Resend!"
              : `Resend in ${resendTimer}s`}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={changeNumber}
              sx={{ textAlign: 'center' }}
            >
              Change your phone number
            </Link>
          </Box>

          {error && (
            <Typography id="otp-error-text" color="error" sx={{ mt: 1, textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          {/* Keep recovery CTA visible here as well (only after conflict) */}
          {showSecondaryCTA && (
            <>
              <Button
                variant="outlined"
                fullWidth
                onClick={startSignInInstead}
                disabled={status === 'verifying'}
                sx={theme => ({
                  backgroundColor: theme.colors.white,
                  color: theme.colors.blue,
                  borderRadius: '50px',
                  border: `1px solid ${theme.colors.blue}`,
                  padding: '12px 30px',
                  mt: 2,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                })}
              >
                Sign in with this phone number
              </Button>
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                We will sign out your current session and sign you in with this phone number.
              </Typography>

              {/* Explicit option to use a different phone number */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleUseDifferentNumber}
                  sx={{ textAlign: 'center' }}
                >
                  Use a different phone number
                </Link>
              </Box>
            </>
          )}
        </>
      )}

      {/* Persistent explanatory message */}
      <Box sx={{ textAlign: 'left', mt: 4 }}>
        <Typography
          variant="subtitle1"
          component="div"
          sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}
        >
          Why verify your phone number?
        </Typography>
        <Typography variant="body2" component="p" sx={{ fontSize: '1rem', my: 1.2 }}>
          To send and receive time capsules with friends. That's it.
        </Typography>
        <Typography variant="body2" component="p" sx={{ fontSize: '1rem', my: 1.2 }}>
          Your number helps us match you with people you know. We'll never use it for anything else.
        </Typography>
        <Typography variant="body2" component="p" sx={{ fontSize: '1rem', my: 1.2 }}>
          You can still explore locations without verifying, but you won't be able to bury or uncover capsules.
        </Typography>

        {/* NEW: Skip / Sign out actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            mt: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="text"
            onClick={handleSkipForNow}
            sx={theme => ({
              color: theme.colors.blue,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '50px',
              px: 2.5,
              py: 1,
            })}
          >
            Skip for now
          </Button>

          <Button
            variant="outlined"
            onClick={handleSignOut}
            sx={theme => ({
              backgroundColor: theme.colors.white,
              color: theme.colors.blue,
              borderRadius: '50px',
              border: `1px solid ${theme.colors.blue}`,
              textTransform: 'none',
              fontWeight: 700,
              px: 2.5,
              py: 1,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            Sign out
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
